import { forwardRef } from "react";
import { FileText, BookOpen, ExternalLink } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * RagSourceChip — KB chunk reference (title + similarity %).
 * Manba: docs/architecture/chatbot-ai-pipeline.md § 6 (RAG search natija).
 *
 * Suggestion ostida ko'rsatiladi — qaysi knowledge chunk ishlatilgan.
 */

export interface RagSourceChipProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "title"> {
  /** Chunk yoki document sarlavhasi */
  title: string;
  /** Similarity 0..1 — % ga aylantiriladi */
  similarity?: number | null;
  /** Optional document title (chunk holatida) */
  documentTitle?: string;
  /** Sahifa raqami (PDF) */
  page?: number | null;
  size?: "sm" | "md";
  /** Tashqi link ikonkasi */
  external?: boolean;
}

const sizeMap = {
  sm: { wrap: "h-5 px-1.5 text-[10px] gap-1", icon: "h-3 w-3" },
  md: { wrap: "h-6 px-2 text-[11px] gap-1.5", icon: "h-3.5 w-3.5" },
} as const;

export const RagSourceChip = forwardRef<HTMLButtonElement, RagSourceChipProps>(
  (
    {
      title,
      similarity,
      documentTitle,
      page,
      size = "md",
      external = false,
      className,
      onClick,
      type,
      ...props
    },
    ref,
  ) => {
    const s = sizeMap[size];
    const pct = similarity != null ? Math.round(similarity * 100) : null;
    const tooltip = [
      documentTitle ? `Hujjat: ${documentTitle}` : null,
      page != null ? `Sahifa: ${page}` : null,
      pct != null ? `O'xshashlik: ${pct}%` : null,
    ]
      .filter(Boolean)
      .join(" · ") || title;

    const isInteractive = typeof onClick === "function";

    const content = (
      <>
        {documentTitle ? (
          <BookOpen className={cn(s.icon, "shrink-0")} aria-hidden />
        ) : (
          <FileText className={cn(s.icon, "shrink-0")} aria-hidden />
        )}
        <span className="truncate max-w-[160px]">{title}</span>
        {pct != null && (
          <span className="font-mono text-[var(--text-muted)] shrink-0">
            {pct}%
          </span>
        )}
        {external && (
          <ExternalLink
            className={cn(s.icon, "shrink-0 opacity-60")}
            aria-hidden
          />
        )}
      </>
    );

    if (isInteractive) {
      return (
        <button
          ref={ref}
          type={type ?? "button"}
          onClick={onClick}
          title={tooltip}
          className={cn(
            "inline-flex items-center rounded-md border font-medium leading-none transition-colors",
            "border-[var(--border-subtle)] bg-[var(--bg-surface)] text-[var(--text-secondary)]",
            "hover:border-[var(--accent-violet)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-violet)]",
            s.wrap,
            className,
          )}
          {...props}
        >
          {content}
        </button>
      );
    }

    return (
      <span
        // when not interactive, ignore button-only props
        title={tooltip}
        className={cn(
          "inline-flex items-center rounded-md border font-medium leading-none",
          "border-[var(--border-subtle)] bg-[var(--bg-surface)] text-[var(--text-secondary)]",
          s.wrap,
          className,
        )}
      >
        {content}
      </span>
    );
  },
);
RagSourceChip.displayName = "RagSourceChip";
